import React, { useMemo, useState } from "react";
import { IndianRupee, Percent, Calendar, Calculator } from "lucide-react";
import ToolCard from "./ToolCard";
import { formatINR } from "./formatters";

function computeEMI(P, annualRate, months) {
  const r = annualRate / 12 / 100;
  if (months <= 0 || P <= 0) return { emi: 0, total: 0, interest: 0 };
  if (r === 0) {
    const emi = P / months;
    return { emi, total: P, interest: 0 };
  }
  const pow = Math.pow(1 + r, months);
  const emi = (P * r * pow) / (pow - 1);
  const total = emi * months;
  const interest = total - P;
  return { emi, total, interest };
}

export default function EMICalculator() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(10.5);
  const [tenureYears, setTenureYears] = useState(5);

  const months = useMemo(() => Math.max(1, Math.round(Number(tenureYears) * 12)), [tenureYears]);
  const { emi, total, interest } = useMemo(
    () => computeEMI(Number(amount), Number(rate), months),
    [amount, rate, months]
  );

  return (
    <ToolCard
      title="EMI Calculator"
      subtitle="Estimate monthly EMI for loans. Great for home/vehicle/personal loans."
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
        <Calculator className="h-4 w-4" />
        Quick estimate
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field
          label="Loan Amount"
          icon={<IndianRupee className="h-4 w-4 text-slate-500" />}
          value={amount}
          onChange={setAmount}
          min={0}
        />
        <Field
          label="Interest Rate (p.a.)"
          icon={<Percent className="h-4 w-4 text-slate-500" />}
          value={rate}
          onChange={setRate}
          min={0}
          step="0.1"
        />
        <Field
          label="Tenure (years)"
          icon={<Calendar className="h-4 w-4 text-slate-500" />}
          value={tenureYears}
          onChange={setTenureYears}
          min={0}
          step="0.5"
        />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <ResultBox title="Monthly EMI" value={formatINR(emi)} highlight />
        <ResultBox title="Total Interest" value={formatINR(interest)} />
        <ResultBox title="Total Payment" value={formatINR(total)} />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Note: This is an estimate. Actual EMI may vary with lender fees and schedule.
      </p>
    </ToolCard>
  );
}

function Field({ label, icon, value, onChange, min = 0, step = "1" }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        {icon}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm outline-none"
          min={min}
          step={step}
        />
      </div>
    </div>
  );
}

function ResultBox({ title, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 ${highlight ? "bg-emerald-50" : "bg-slate-50"}`}>
      <div className={`text-xs ${highlight ? "text-emerald-900/70" : "text-slate-600"}`}>{title}</div>
      <div className={`mt-1 text-lg font-semibold ${highlight ? "text-emerald-950" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
